import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { match } from 'ts-pattern';

import { reportEvent } from '~/domains/analytics/events';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import useRedirectToEO from '~/domains/common/utils/useRedirectToEO';
import { useIsNotebook } from '~/domains/common/hooks/mediaQueries';
import { ContestTab } from '~/domains/contest/domains/entries/hooks/useEntriesTabsProps';
import { getContestNavigationTarget } from '~/domains/contest/domains/common/utils/getContestNavigationTarget';
import { getVisibleUserEntriesCount } from '~/domains/contest/domains/common/utils/pickControl';
import useEntries from '~/domains/contest/domains/entries/hooks/useEntries';
import { ContestStatusFilter } from '~/domains/contest/domains/services/enums/contest-status-filter.enum';

export const useHomeNotifications = () => {
  const router = useRouter();
  const isNotebook = useIsNotebook();
  const { redirectToEO, isFetching, isRedirecting } = useRedirectToEO();

  const {
    contests: contestData,
    isLoading: isContestDataLoading,
    itemsTotal: totalContests,
  } = useEntries({
    status: ContestStatusFilter.ACTION_REQUIRED,
    limit: 3,
  });

  const notificationsProps = useMemo(() => {
    if (isContestDataLoading || !contestData) return;
    if (totalContests === 0) return;

    // Only show 3 notifications on desktops
    const contestDataArray = isNotebook ? contestData.slice(0, 2) : contestData;

    return {
      content: {
        type: 'notifications',
        notifications: isContestDataLoading
          ? []
          : contestDataArray.map((contest) => ({
              id: contest.id,
              alerts: contest.alerts?.values,
              createdAt: new Date(),
              contest,
            })),
        onNotificationClick: (notification) => {
          const { contest } = notification;
          const target = getContestNavigationTarget(contest);

          match(target)
            .with({ kind: 'eo' }, ({ url, brand }) => {
              if (isFetching || isRedirecting) return;

              reportEvent('Home > Notifications > Click', {
                count: totalContests,
                isEO: true,
                brand,
              });
              redirectToEO(url);
            })
            .with({ kind: 'splashV2' }, ({ url }) => {
              reportEvent('Home > Notifications > Click', { count: totalContests, isEO: false });
              window.location.assign(url);
            })
            .with({ kind: 'internal' }, () => {
              reportEvent('Home > Notifications > Click', { count: totalContests, isEO: false });
              router.push(
                Routes.gamecenter({
                  contestId: contest.id,
                  contestStatus: contest.status,
                  userEntriesCount: getVisibleUserEntriesCount(contest),
                  contestType: contest.contest_type,
                  currentSlateStatus: contest.current_slate_status,
                })
              );
            })
            .with({ kind: 'none' }, () => undefined)
            .exhaustive();
        },
      },
      totalCount: totalContests,
      onViewAllClick: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        reportEvent('Home > Notifications > View All', { count: totalContests });

        router.push(
          Routes.entries({
            [URLQueryKeys.CONTEST_TAB]: ContestTab.ACTIVE,
            [URLQueryKeys.CONTEST_STATUS]: ContestStatusFilter.ACTION_REQUIRED,
          })
        );
      },
    };
  }, [
    contestData,
    isContestDataLoading,
    totalContests,
    router,
    isNotebook,
    isFetching,
    isRedirecting,
    redirectToEO,
  ]);

  return {
    notificationsProps,
    isNotificationsLoading: isContestDataLoading,
  };
};
